import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type ILpcoResponse } from '../../modules/License/types/response';

import {
  type IAuditResponse,
  type IDifference,
  type IDocumentHistoryItem,
  type IDocumentHistoryResponse,
} from './types';

import createMapByProperty from '../../helpers/createMapByProperty';

export interface IAuditState {
  documentHistory: {
    data: IDocumentHistoryItem[];
    isLoading: boolean;
    licenceTypeId: number | null;
  };
  versionComparison: {
    differences: IDifference[];
    differenceMap: Record<string, IDifference>;
    lpcoResponse: Partial<ILpcoResponse>;
    isLoading: boolean;
  };
}

const initialState: IAuditState = {
  documentHistory: {
    data: [],
    isLoading: false,
    licenceTypeId: null,
  },
  versionComparison: {
    differences: [],
    differenceMap: {},
    lpcoResponse: {},
    isLoading: false,
  },
};

export const audit = createSlice({
  name: 'audit',
  initialState,
  reducers: {
    setDocumentHistory: (
      state,
      { payload: { versionListResponse, licenceTypeId } }: PayloadAction<IDocumentHistoryResponse>
    ) => {
      state.documentHistory.data = versionListResponse.reverse();
      state.documentHistory.licenceTypeId = licenceTypeId;
    },
    setDocumentHistoryLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.documentHistory.isLoading = payload;
    },
    resetDocumentHistory: (state) => {
      state.documentHistory.data = [];
      state.documentHistory.licenceTypeId = null;
    },
    setVersionComparison: (state, { payload }: PayloadAction<IAuditResponse>) => {
      const { differences, lpcoResponse } = payload;
      state.versionComparison.lpcoResponse = lpcoResponse;
      state.versionComparison.differences = differences;
      state.versionComparison.differenceMap = createMapByProperty(differences, 'path');
    },
    setVersionComparisonLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.versionComparison.isLoading = payload;
    },
    resetVersionComparison: (state) => {
      state.versionComparison.differences = [];
      state.versionComparison.differenceMap = {};
      state.versionComparison.lpcoResponse = {};
    },
  },
});

export const {
  setDocumentHistory,
  setDocumentHistoryLoading,
  resetDocumentHistory,
  setVersionComparison,
  setVersionComparisonLoading,
  resetVersionComparison,
} = audit.actions;
